import i18n from "@/i18n";
/**
 * Prompt 14D — Rules Engine display helpers (read-only).
 *
 * Pure functions. No React, no network. Manager-facing labels only.
 * Every duration is expressed via `formatDurationMinutes` — raw
 * integer minutes must never appear in the manager UI. No monetary
 * computation happens here or downstream.
 */
import { formatDurationMinutes } from "./workforceHelpers";

// ─── Rules status codes (mirror backend) ─────────────────────
export const RULE_STATUS_STANDARD_APPLIED = "STANDARD_RULES_APPLIED";
export const RULE_STATUS_NOT_CONFIGURED = "RULE_VERSION_NOT_CONFIGURED";
export const RULE_STATUS_INCOMPLETE = "RULE_VERSION_CONFIGURATION_INCOMPLETE";
export const RULE_STATUS_AVERAGING_NOT_YET =
    "AVERAGING_RULES_NOT_YET_IMPLEMENTED";
export const RULE_STATUS_CALENDAR_NOT_COVERED = "LEGAL_CALENDAR_NOT_COVERED";
// STANDARD shared employee counter (one three-week calendar per employment).
export const STANDARD_PENDING_CODES = new Set([
    "STANDARD_CROSS_RESTAURANT_PENDING", "STANDARD_SOURCES_PENDING",
]);
const STANDARD_STATUS_TEXT = {
    get STANDARD_CROSS_RESTAURANT_PENDING() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_CROSS_RESTAURANT_PENDING", "Pending — hours at another restaurant still need review."); },
    get STANDARD_SOURCES_PENDING() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_SOURCES_PENDING", "Pending — earlier hours still need review."); },
    get STANDARD_CALENDAR_ANCHOR_UNRESOLVED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_CALENDAR_ANCHOR_UNRESOLVED", "Not calculated — the employee's three-week calendar is not configured."); },
    get STANDARD_CALENDAR_ANCHOR_CONFLICT() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_CALENDAR_ANCHOR_CONFLICT", "Not calculated — the employee's three-week calendar conflicts with an earlier Close."); },
    get STANDARD_EMPLOYMENT_NOT_ESTABLISHED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_EMPLOYMENT_NOT_ESTABLISHED", "Not calculated — the employment for these dates is not established."); },
    get STANDARD_EMPLOYMENT_MODEL_NOT_STANDARD() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_EMPLOYMENT_MODEL_NOT_STANDARD", "Not calculated — the employment does not use standard working time."); },
    get STANDARD_TERMS_CHANGED_IN_PERIOD_UNRESOLVED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_TERMS_CHANGED_IN_PERIOD_UNRESOLVED", "Not calculated — the contract changes inside the three-week period."); },
    get STANDARD_PARTIAL_PERIOD_UNRESOLVED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_PARTIAL_PERIOD_UNRESOLVED", "Not calculated — incomplete three-week period (TES 18 § 3 not supported)."); },
    get STANDARD_PART_TIME_ROSTER_UNRESOLVED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_PART_TIME_ROSTER_UNRESOLVED", "Not calculated — the roster differs from the part-time contract hours."); },
    get STANDARD_RULE_VERSION_NOT_CONFIGURED() { return i18n.t("wf.workforceRules.STANDARD_STATUS_TEXT.STANDARD_RULE_VERSION_NOT_CONFIGURED", "Rules are not configured yet."); },
};
export const PROVISIONAL_CALENDAR_TEXT =
    "Provisional — the three-week calendar is confirmed at Close.";

// ─── Classification label mapping (§14) ──────────────────────
const CLASSIFICATION_LABELS = {
    get REGULAR() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.REGULAR", "Regular hours"); },
    get ADDITIONAL() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.ADDITIONAL", "Additional work"); },
    get OVERTIME_50() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.OVERTIME_50", "Overtime +50%"); },
    get OVERTIME_100() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.OVERTIME_100", "Overtime +100%"); },
    get EVENING() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.EVENING", "Evening"); },
    get NIGHT() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.NIGHT", "Night"); },
    get SUNDAY() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.SUNDAY", "Sunday"); },
    get PUBLIC_HOLIDAY() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.PUBLIC_HOLIDAY", "Public holiday"); },
    get EVE() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.EVE", "Holiday eve +50%"); },
    get REST_DAY_50() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.REST_DAY_50", "Rest-day work +50%"); },
    get WEEKLY_REST_100() { return i18n.t("wf.workforceRules.CLASSIFICATION_LABELS.WEEKLY_REST_100", "Weekly-rest work +100%"); },
};

export function classificationLabel(category) {
    return CLASSIFICATION_LABELS[category] || "";
}

// ─── Compliance-warning label mapping (§15) ──────────────────
const WARNING_LABELS = {
    get SHIFT_OVER_10H_CONSENT_REQUIRED() { return i18n.t("wf.workforceRules.WARNING_LABELS.SHIFT_OVER_10H_CONSENT_REQUIRED", "Shift over 10 h — employee consent must be confirmed"); },
    get SHIFT_OVER_16H_NOT_ALLOWED() { return i18n.t("wf.workforceRules.WARNING_LABELS.SHIFT_OVER_16H_NOT_ALLOWED", "Shift exceeds 16 h"); },
    get REST_BELOW_11H() { return i18n.t("wf.workforceRules.WARNING_LABELS.REST_BELOW_11H", "Rest between shifts is below 11 h"); },
    get REST_BELOW_8H_NOT_ALLOWED() { return i18n.t("wf.workforceRules.WARNING_LABELS.REST_BELOW_8H_NOT_ALLOWED", "Rest between shifts is below 8 h"); },
    get MAX_7_CONSECUTIVE_WORKDAYS() { return i18n.t("wf.workforceRules.WARNING_LABELS.MAX_7_CONSECUTIVE_WORKDAYS", "More than 7 consecutive workdays"); },
    get MAX_15_WORKDAYS_PER_PERIOD() { return i18n.t("wf.workforceRules.WARNING_LABELS.MAX_15_WORKDAYS_PER_PERIOD", "More than 15 workdays in this period"); },
    get V_REST_BELOW_30H() { return i18n.t("wf.workforceRules.WARNING_LABELS.V_REST_BELOW_30H", "Weekly rest is below 30 h"); },
    get X_REST_BELOW_24H() { return i18n.t("wf.workforceRules.WARNING_LABELS.X_REST_BELOW_24H", "Additional rest is below 24 h"); },
    get MEAL_BREAK_REVIEW_REQUIRED_OVER_6H() { return i18n.t("wf.workforceRules.WARNING_LABELS.MEAL_BREAK_REVIEW_REQUIRED_OVER_6H", "Meal-break arrangement should be checked"); },
    get LONG_SHIFT_BREAK_REVIEW_OVER_10H() { return i18n.t("wf.workforceRules.WARNING_LABELS.LONG_SHIFT_BREAK_REVIEW_OVER_10H", "Long-shift break arrangement should be checked"); },
};

export function warningLabel(code) {
    return WARNING_LABELS[code] || "";
}

// ─── Per-employee helpers ────────────────────────────────────
/**
 * Returns the rules-status entry for one employee, or a safe empty
 * shape if none is present. Never invents a status.
 */
export function rulesForEmployee(rulesPayload, employeeId) {
    if (!rulesPayload || !rulesPayload.rules_status_by_employee) {
        return null;
    }
    return rulesPayload.rules_status_by_employee[employeeId] || null;
}

/**
 * Restrict rules to a single (employee_id, work_date) cell using
 * source_actual_entry_id → actual_entry.work_date mapping.
 *
 *   actualIdsForDate: Set<string> · canonical Actual Entry IDs for
 *   the specific cell.
 */
export function rulesForCell(entry, actualIdsForDate) {
    if (!entry) return null;
    const ids = actualIdsForDate instanceof Set
        ? actualIdsForDate
        : new Set(actualIdsForDate || []);
    const classifications = (entry.classifications || []).filter(
        (c) => ids.has(c.source_actual_entry_id));
    const warnings = (entry.compliance_warnings || []).filter((w) => (
        // Period-wide warnings (no actual_entry_id / no work_date)
        // are NOT displayed inside a per-cell "Rules" section — they
        // belong at the employee-row summary level. Here we only
        // include cell-scoped warnings whose Actual Entry belongs to
        // this cell.
        w.actual_entry_id && ids.has(w.actual_entry_id)
    ));
    const std = entry.standard || null;
    let status = entry.rules_status;
    if (STANDARD_PENDING_CODES.has(status) && std) {
        // Only cells whose own entries wait for earlier reviews are pending.
        const pending = new Set(std.pending_entry_ids || []);
        if (![...ids].some((id) => pending.has(id))) {
            status = RULE_STATUS_STANDARD_APPLIED;
        }
    }
    return {
        rules_status: status,
        classifications,
        compliance_warnings: warnings,
        provisional: !!(std && std.provisional),
    };
}

/**
 * True when a per-cell rules payload has any manager-facing signal:
 *   • any non-REGULAR classification;
 *   • any compliance warning; or
 *   • a non-STANDARD_RULES_APPLIED rules_status.
 * REGULAR-only classifications with no warning MUST NOT trigger a
 * grid indicator (§13).
 */
export function cellHasRulesSignal(cellRules) {
    if (!cellRules) return false;
    if (cellRules.rules_status && cellRules.rules_status !== RULE_STATUS_STANDARD_APPLIED) {
        return true;
    }
    if ((cellRules.compliance_warnings || []).length > 0) return true;
    return (cellRules.classifications || []).some(
        (c) => c.category !== "REGULAR");
}

/**
 * Sum classification minutes per category. Overlay categories are
 * kept alongside base categories intentionally — the manager reads
 * the display, the backend still owns the canonical accounting.
 */
export function aggregateClassifications(classifications) {
    const totals = {};
    for (const c of classifications || []) {
        totals[c.category] = (totals[c.category] || 0) + c.minutes;
    }
    return totals;
}

/**
 * Human-readable rows for the ActualCellEditor Rules section:
 *   [{ category, label, minutes, duration_text }, ...]
 * Ordering is deterministic: base categories first (REGULAR,
 * ADDITIONAL, OVERTIME_50, OVERTIME_100) followed by overlays in a
 * fixed reading order.
 */
const _ROW_ORDER = [
    "REGULAR", "ADDITIONAL", "OVERTIME_50", "OVERTIME_100",
    "EVENING", "NIGHT", "SUNDAY", "PUBLIC_HOLIDAY", "EVE",
    "REST_DAY_50", "WEEKLY_REST_100",
];

export function classificationRows(classifications) {
    const totals = aggregateClassifications(classifications);
    const rows = [];
    for (const cat of _ROW_ORDER) {
        if (totals[cat] > 0) {
            rows.push({
                category: cat,
                label: classificationLabel(cat),
                minutes: totals[cat],
                duration_text: formatDurationMinutes(totals[cat]),
            });
        }
    }
    return rows;
}

export function warningRows(warnings) {
    // Deduplicate — the backend may emit the same code more than
    // once per cell (e.g. rest & long-shift on a 10 h+ shift).
    const seen = new Set();
    const out = [];
    for (const w of warnings || []) {
        if (seen.has(w.code)) continue;
        const label = warningLabel(w.code);
        if (!label) continue;
        seen.add(w.code);
        out.push({ code: w.code, label });
    }
    return out;
}

/**
 * Return the safe status text for a non-STANDARD_RULES_APPLIED
 * rules_status. Null when nothing safe should be shown (either the
 * standard applied happily, or the status is unknown).
 */
export function statusSafeMessage(rules_status) {
    if (rules_status === RULE_STATUS_NOT_CONFIGURED) {
        return i18n.t("wf.workforceRules.rulesAreNotConfiguredYet", "Rules are not configured yet.");
    }
    if (rules_status === RULE_STATUS_INCOMPLETE) {
        return i18n.t("wf.workforceRules.rulesConfigurationIsIncomplete", "Rules configuration is incomplete.");
    }
    if (rules_status === RULE_STATUS_AVERAGING_NOT_YET) {
        return i18n.t("wf.workforceRules.averagingRulesAreNotAvailableYet", "Averaging rules are not available yet.");
    }
    if (rules_status === RULE_STATUS_CALENDAR_NOT_COVERED) {
        return i18n.t("wf.workforceRules.holidayCalendarDoesNotCoverThese", "Holiday calendar does not cover these dates yet.");
    }
    return STANDARD_STATUS_TEXT[rules_status] || null;
}
